// ./src/components/features/AccountEmails/AccountEmailSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useAccountEmails } from "@/hooks/useAccountEmails";
import { LoadingIndicator } from "@/components/generic";
import { AccountEmail } from "@/utils/api";
import { useApp } from "@/contexts";
import { STATUSES } from "@sdflc/utils";

export interface AccountEmailSelectorProps {
  /** Selected account email ID */
  value: string | null | undefined;

  /** Callback when selection changes */
  onChange?: (
    accountEmailId: string | null,
    accountEmail?: AccountEmail
  ) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to show only confirmed emails */
  confirmedOnly?: boolean;

  /** Whether to include inactive emails */
  includeInactive?: boolean;

  /** Custom filter function */
  emailFilter?: (accountEmail: AccountEmail) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Field label */
  label?: string;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const AccountEmailSelector: React.FC<AccountEmailSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select an email",
  confirmedOnly = false,
  includeInactive = true,
  emailFilter,
  required = false,
  label,
  loadingPlaceholder = "Loading emails...",
  emptyText = "No emails available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();
  const { accountEmails, loading, isEmpty, lastError } = useAccountEmails({
    autoLoad: true,
    userId: user?.id,
    initialFilters: includeInactive
      ? {}
      : { status: [STATUSES.ACTIVE, STATUSES.PENDING] },
  });

  // Filter and prepare options
  const emailOptions = useMemo(() => {
    let filteredEmails = accountEmails;

    // Filter by confirmed status if required
    if (confirmedOnly) {
      filteredEmails = filteredEmails.filter((email) => !!email.confirmedAt);
    }

    // Apply custom filter if provided
    if (emailFilter) {
      filteredEmails = filteredEmails.filter(emailFilter);
    }

    // Convert to select options
    return filteredEmails.map((accountEmail) => {
      const isConfirmed = !!accountEmail.confirmedAt;
      const statusLabel = isConfirmed ? "" : " (pending)";
      const description = accountEmail.description
        ? ` - ${accountEmail.description}`
        : "";

      return {
        value: accountEmail.id || "",
        label: `${accountEmail.email}${statusLabel}${description}`,
        disabled: accountEmail.status !== STATUSES.ACTIVE,
      };
    });
  }, [accountEmails, confirmedOnly, emailFilter]);

  // Create a map for quick lookup by ID
  const emailMap = useMemo(() => {
    const map = new Map<string, AccountEmail>();
    accountEmails.forEach((email) => {
      if (email.id) {
        map.set(email.id, email);
      }
    });
    return map;
  }, [accountEmails]);

  // Auto-select first email if required and no value provided
  useEffect(() => {
    if (required && !value && emailOptions.length > 0 && !loading && onChange) {
      const firstOption = emailOptions.find((opt) => !opt.disabled);
      if (firstOption) {
        const emailObject = emailMap.get(firstOption.value);
        onChange(firstOption.value, emailObject);
      }
    }
  }, [required, value, emailOptions, loading, onChange, emailMap]);

  // Handle onChange with object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedEmail = emailMap.get(selectedValue);
      onChange?.(selectedValue, selectedEmail);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Determine select state
  const isInitialLoading = loading && accountEmails.length === 0;
  const hasError = lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  const selectState = {
    data: isInitialLoading || hasError || isEmptyState ? [] : emailOptions,
    placeholder: isInitialLoading
      ? loadingPlaceholder
      : hasError
      ? "Error loading emails"
      : isEmptyState
      ? emptyText
      : placeholder,
    disabled: disabled || isInitialLoading || hasError || isEmptyState,
    error: hasError ? error || "Failed to load emails" : error,
    rightSection:
      isInitialLoading || (loading && accountEmails.length > 0) ? (
        <LoadingIndicator size={size} color="gray" />
      ) : (
        selectProps.rightSection
      ),
  };

  return (
    <Select
      {...selectProps}
      label={label}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error}
      rightSection={selectState.rightSection}
    />
  );
};
